import { SchemeOptional } from "@/type/commonTypes";
import { useAppDispatch } from "@/redux/Hooks";
import { FundCardData } from "@/type/comparePageTypes";
import Link from "next/link";
import Image from "next/image";
import { openInvestmentModal } from "@/redux/features/InvestmentModal/investmentModalSlice";
import { useIsMobile } from "@/utils/useIsMobile";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "@/components/icons/Icons";
import { useLoginModal } from "@/utils/useLoginModal";
import { ROOT_URL } from "@/utils/gloableVariables";

type FundCardProps = {
  data: FundCardData;
  autoOpen: boolean;
};

export default function VerdictCard({ data, autoOpen }: FundCardProps) { 
  const isBestPick = data.isBestPick === true;
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);

  const isMob = useIsMobile(631);
  const { isLoggedIn, open, setModalKey } = useLoginModal();

  useEffect(() => {
    setLoading(false);
  }, [isMob]);

  const [isOpen, setIsOpen] = useState<boolean>(autoOpen);

  const formPlayload: SchemeOptional = {
    scheme_code: data.scheme_code || "",
    scheme_name: data.scheme_name || "",
    amc_code: data.amc_code || "",
    sip_allowed: data.sip_allowed,
    lump_allowed: data.lump_allowed,
    minimum_sip_invest: data.minimum_sip_invest,
    minimum_investment: data.minimum_investment,
  };

  const toggleAccordion = () => {
    if (!isMob) return;
    setIsOpen((prev) => !prev);
  };

  return (
    <div className={`w-full shrink-0 max-w-96 xs:max-w-70 rounded-4xl relative ${isBestPick ? "bg-[#EDF4FF] border-2 border-primaryBlue 2lg:scale-105 2lg:shadow-[0px_4px_27.1px_0px_#1A51AA40]" : "bg-white border border-[#1A51AA2E]"}`}>
      {isBestPick && <span className="max-w-fit rounded-full inline-block absolute top-0 inset-x-0 mx-auto -translate-y-1/2 bg-primaryBlue text-white italic text-xs leading-1 font-bold uppercase px-7 py-1.5 shadow-[0px_10.2px_15.3px_-3.06px_#1a51aa1a]">MYSIP Pick</span>}

      <div className="p-4 2lg:p-5">
        {loading ? (
          <div className="flex items-center gap-2 animate-pulse h-10 xs:h-half-px">
            <div className="h-8 w-8 rounded-full bg-[#E9E9EB] shrink-0" />
            <div className="flex-1 h-4 rounded-sm bg-[#E9E9EB]" />
          </div>
        ) : isMob ? (
          <button onClick={toggleAccordion} className="flex xs:hidden items-center gap-2 group h-10">
            <Image src={`${ROOT_URL}/img/AMCsLogo/${data.amc_code}.png`} alt={data.scheme_name} width={36} height={36} className="h-9 w-9 shrink-0 rounded-full object-contain transition border border-[#E9E9EB] group-hover:border-primaryBlue/40" />

            <p title={data.scheme_name} className="text-sm sm:text-base leading-1.4! h-10 sm:h-12 font-semibold text-left line-clamp-2">
              {data.scheme_name}
            </p>

            <Icon.dropdownDownArrow className={`shrink-0 transition duration-300 ease-linear ${isOpen ? "rotate-180" : ""}`} />
          </button>
        ) : (
          <Link href={`${ROOT_URL}mutual-fund/${data.url}/mso${data.id}`} className="hidden xs:flex items-center gap-2 group h-half-px">
            <Image src={`${ROOT_URL}/img/AMCsLogo/${data.amc_code}.png`} alt={data.scheme_name} width={36} height={36} className="h-9 w-9 shrink-0 rounded-full object-contain transition border border-[#E9E9EB] group-hover:border-primaryBlue/40" />

            <p title={data.scheme_name} className="text-sm sm:text-base leading-1.4! h-10 sm:h-12 font-semibold line-clamp-2 transition group-hover:text-primaryBlue">
              {data.scheme_name}
            </p>
          </Link>
        )}
      </div>

      <AnimatePresence initial={false}>
        {(!isMob || isOpen) && (
          <motion.div
            initial={isMob ? { height: 0, opacity: 0 } : undefined}
            animate={{ height: "auto", opacity: 1 }}
            exit={isMob ? { height: 0, opacity: 0 } : undefined}
            transition={{
              height: { duration: 0.35, ease: "easeInOut" },
              opacity: { duration: 0.2 },
            }}
            className={`overflow-hidden ${isOpen ? "max-h-none" : "max-h-0 xs:max-h-none"}`}
          >
            <div className="p-4 2lg:p-5 pt-0 2lg:pt-0">
              <div className={`flex items-center justify-around border border-dashed border-[#C7D8F2] text-center rounded-lg px-4 py-3 ${isBestPick ? "bg-white" : "bg-[#F4F7FD]"}`}>
                <div className="space-y-2">
                  <div className="text-sm leading-1.1 text-black/70">Returns ({data.returnLabel})</div>
                  <span className={`text-sm font-bold leading-1.1 ${data.returns >= 0 ? "text-[#138000]" : "text-[#ff3030]"}`}>{data.returns} %</span>
                </div>

                <div className="space-y-2">
                  <div className="text-sm leading-1.1 text-black/70">Expense Ratio</div>
                  <span className="text-sm font-bold leading-1.1 text-black">{data.expenseRatio} %</span>
                </div>
              </div>

              {isLoggedIn && (
                <button onClick={() => dispatch(openInvestmentModal(formPlayload))} className={`w-full mt-4.5 rounded-xl font-semibold border border-primaryBlue text-sm sm:text-base leading-1.2! p-3 transition duration-300 ${isBestPick ? "bg-primaryBlue text-white hover:bg-white hover:text-primaryBlue" : "text-primaryBlue hover:bg-primaryBlue hover:text-white"}`}>
                  Start SIP
                </button>
              )}
            </div>

            <div className={`p-3.5 sm:p-4 border-t rounded-b-4xl relative ${isBestPick ? "border-primaryBlue bg-[#1A51AA14]" : "border-[#1A51AA2E]"}`}>
              <div className={`font-bold text-sm leading-1 ${isBestPick ? "text-primaryBlue" : "text-[#314158]"}`}>{data.footerTitle}</div>

              <p className="text-xs text-[#45556C] leading-1.3 mt-2">{isLoggedIn ? data.footerText : "Log in to reveal the best fund for you — carefully selected using your personalized MYSIP suggestions."}</p>

              {!isLoggedIn && (
                <div className="absolute inset-0 rounded-b-4xl flex flex-col items-center justify-center gap-3 bg-white/85 backdrop-blur-xs p-3">
                  <div className="flex items-center gap-2 text-[#8AA6D5] font-semibold">
                    <Icon.lockIconV4 className="w-5 h-5 text-inherit" />
                    Verdict Lock
                  </div>
                  <button
                    onClick={() => {
                      open();
                      setModalKey("best-pick-compare-page");
                    }}
                    className="w-full rounded-lg font-semibold border border-primaryBlue text-2xs sm:text-2sm leading-1.3! p-2 transition duration-300 text-primaryBlue hover:bg-primaryBlue hover:text-white"
                  >
                    Reveal Winner
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
